import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleCheck, XCircle, AlertCircle, PlayCircle, BookOpen, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { postRequest } from '../utils/networking';
import { apiUrls } from '../utils/url';
import CourseHeader from '../components/CourseHeader';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed, error
  const [paymentType, setPaymentType] = useState('course'); // course, webinar
  const [orderId, setOrderId] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const transactionId = 
        searchParams.get('transactionId') || 
        searchParams.get('merchantTransactionId') || 
        searchParams.get('id');

      if (!transactionId) {
        setStatus('error');
        setMessage('No transaction identifier found in redirect URL.');
        return;
      }

      setOrderId(transactionId);

      // Determine payment type based on prefix
      let verifyUrl = '';
      let payload = { transactionId };

      if (transactionId.startsWith('C_')) {
        setPaymentType('course');
        verifyUrl = apiUrls.verifycoursepayment;
        
        // Retrieve fallback metadata
        const courseId = localStorage.getItem('pending_course_id');
        let email = localStorage.getItem('pending_email');
        const phone = localStorage.getItem('pending_phone');

        // Dynamically get logged-in user email if pending_email is missing or default
        if (!email || email === 'student@example.com') {
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const userObj = JSON.parse(userStr);
              if (userObj && (userObj.email || userObj.useremail)) {
                email = userObj.email || userObj.useremail;
              }
            }
          } catch (e) {
            console.error("Error reading user from localStorage:", e);
          }
        }

        payload.courseid = courseId ? parseInt(courseId) : null;
        if (email) payload.email = email;
        if (phone) payload.phone = phone;
      } else if (transactionId.startsWith('W_')) {
        setPaymentType('webinar');
        verifyUrl = apiUrls.verifywebinarpayment;

        // Retrieve fallback metadata
        const webinarId = localStorage.getItem('pending_webinar_id');
        const email = localStorage.getItem('pending_email');
        const phone = localStorage.getItem('pending_phone');
        payload.webinarid = webinarId ? parseInt(webinarId) : null;
        payload.email = email || 'user@example.com';
        payload.phone = phone || null;
      } else {
        setStatus('error');
        setMessage('Invalid transaction format.');
        return;
      }

      try {
        console.log(`📡 Verifying ${paymentType} payment...`, payload);
        const verifyRes = await postRequest(verifyUrl, payload);
        console.log("PhonePe Payment Verification Response:", verifyRes);

        if (verifyRes?.status === 'S' || verifyRes?.status === 's') {
          setStatus('success');
          
          // Clear verified items from localStorage
          localStorage.removeItem('pending_payment_type');
          localStorage.removeItem('pending_course_id');
          localStorage.removeItem('pending_webinar_id');
          localStorage.removeItem('pending_phone');
          localStorage.removeItem('pending_email');

          if (transactionId.startsWith('C_')) {
            setTimeout(() => {
              window.location.href = "https://chat.whatsapp.com/GPymhKiAK4MCNei0iSDnCw";
            }, 1500);
          }
        } else {
          // start polling fallback if not success
          setStatus('verifying');
          setMessage('Verification pending, will retry...');
          // start polling attempts
          let attempts = 0;
          const maxAttempts = 5;
          const intervalId = setInterval(async () => {
            attempts++;
            try {
              const pollRes = await postRequest(verifyUrl, payload);
              if (pollRes?.status === 'S' || pollRes?.status === 's') {
                clearInterval(intervalId);
                setStatus('success');
                setMessage('');
                localStorage.removeItem('pending_payment_type');
                localStorage.removeItem('pending_course_id');
                localStorage.removeItem('pending_webinar_id');
                localStorage.removeItem('pending_phone');
                localStorage.removeItem('pending_email');

                if (transactionId.startsWith('C_')) {
                  setTimeout(() => {
                    window.location.href = "https://chat.whatsapp.com/GPymhKiAK4MCNei0iSDnCw";
                  }, 1500);
                }
              } else if (attempts >= maxAttempts) {
                clearInterval(intervalId);
                setStatus('failed');
                setMessage(pollRes?.message || 'Payment verification failed after retries.');
              }
            } catch (pollErr) {
              console.error('Polling error:', pollErr);
              if (attempts >= maxAttempts) {
                clearInterval(intervalId);
                setStatus('error');
                setMessage('Network error during verification retries.');
              }
            }
          }, 3000);
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
        setMessage('A network error occurred while verifying your payment.');
      }
    };

    verifyPayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-[poppins] text-slate-900">
      <CourseHeader />

      <div className="flex-1 flex items-center justify-center p-4 py-24">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-slate-100 shadow-xl shadow-slate-100/50 text-center">
          {status === 'verifying' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="relative w-20 h-20 mx-auto flex items-center justify-center bg-amber-50 text-amber-500 rounded-full">
                <Loader2 className="w-10 h-10 animate-spin" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-slate-900">Verifying Payment</h2>
                <p className="text-sm text-slate-500 max-w-xs mx-auto">
                  Please wait while we confirm your enrollment status with PhonePe...
                </p>
              </div>
            </motion.div>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="relative w-20 h-20 mx-auto flex items-center justify-center bg-emerald-50 text-emerald-500 rounded-full animate-bounce">
                <CircleCheck className="w-12 h-12" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-slate-900">Payment Successful</h2>
                {orderId && (
                  <p className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full inline-block">
                    Order ID: {orderId}
                  </p>
                )}
                <p className="text-sm text-slate-500 pt-2">
                  Thank you for your purchase. Your registration has been confirmed successfully!
                </p>
              </div>

              <div className="pt-4 space-y-3">
                {paymentType === 'course' ? (
                  <button
                    onClick={() => window.location.href = "https://chat.whatsapp.com/GPymhKiAK4MCNei0iSDnCw"}
                    className="w-full py-3.5 bg-slate-900 text-white font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 hover:bg-slate-800"
                  >
                    <BookOpen className="w-5 h-5" />
                    Join WhatsApp Group
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/courses')}
                    className="w-full py-3.5 bg-slate-900 text-white font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 hover:bg-slate-800"
                  >
                    <PlayCircle className="w-5 h-5" />
                    Go to Dashboard
                  </button>
                )}
                <button
                  onClick={() => navigate('/courses')}
                  className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  Continue Learning
                </button>
              </div>
            </motion.div>
          )}

          {status === 'failed' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="relative w-20 h-20 mx-auto flex items-center justify-center bg-rose-50 text-rose-500 rounded-full">
                <XCircle className="w-12 h-12" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-slate-900">Payment Failed</h2>
                <p className="text-sm text-slate-500 max-w-xs mx-auto">
                  {message || 'The transaction was not successful.'}
                </p>
              </div>

              <div className="pt-4 space-y-3">
                <button
                  onClick={() => navigate('/courses')}
                  className="w-full py-3.5 bg-slate-900 text-white font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 hover:bg-slate-800"
                >
                  Retry Payment
                </button>
                <button
                  onClick={() => navigate('/courses')}
                  className="w-full py-3.5 bg-slate-100 text-slate-700 font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 hover:bg-slate-200"
                >
                  Back to Courses
                </button>
              </div>
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="relative w-20 h-20 mx-auto flex items-center justify-center bg-amber-50 text-amber-500 rounded-full">
                <AlertCircle className="w-12 h-12" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-slate-900">Verification Error</h2>
                <p className="text-sm text-slate-500 max-w-xs mx-auto">
                  {message || 'Could not verify your payment status.'}
                </p>
              </div>

              <div className="pt-4 space-y-3">
                <button
                  onClick={() => navigate('/Mycourse')}
                  className="w-full py-3.5 bg-slate-900 text-white font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 hover:bg-slate-800"
                >
                  Check Course Access
                </button>
                <button
                  onClick={() => navigate('/courses')}
                  className="w-full py-3.5 bg-slate-100 text-slate-700 font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 hover:bg-slate-200"
                >
                  Back to Courses
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
